import { useMemo, useState, ReactNode } from "react";

import { SearchQueryOrderDirection } from "types/generated";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import FiltersSortHeaderSortableColumn from "components/Filters/SortHeader/SortableColumn";
import { SortOption } from "components/Filters/types";
import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import Typography from "ds/components/Typography";
import { Spacing } from "types/Spacing";
import Box from "ds/components/Box";

import styles from "./styles.module.css";
import { sortByField } from "./helpers";

export type SortableTableColumn = SortOption & { static?: boolean; colStart?: string };

export type SortableTableProps<T> = {
  columns: SortableTableColumn[];
  initialSortBy: string;
  initialDirection: SearchQueryOrderDirection;
  items: T[];
  renderItems: (sorted: T[]) => ReactNode;
  columnOrder: string;
  columnGap?: Spacing;
  stickyHeader?: boolean;
  ariaLabel: string;
};

const SortableTable = <T,>({
  columnOrder,
  items,
  columns,
  initialSortBy,
  initialDirection,
  renderItems,
  stickyHeader,
  columnGap = "large",
  ariaLabel,
}: SortableTableProps<T>) => {
  const [direction, setDirection] = useState<SearchQueryOrderDirection>(initialDirection);
  const [sortBy, setSortBy] = useState(initialSortBy);

  const sorted = useMemo(() => sortByField(items, sortBy, direction), [items, sortBy, direction]);

  const handleChangeSorting = (field: string) => () => {
    if (field === sortBy) {
      setDirection((prev) =>
        prev === SearchQueryOrderDirection.Asc
          ? SearchQueryOrderDirection.Desc
          : SearchQueryOrderDirection.Asc
      );
    } else {
      setSortBy(field);
    }
  };
  return (
    <Box role="table" aria-label={ariaLabel} direction="column" grow="1">
      {sorted.length > 0 && (
        <FiltersSortHeaderWrapper
          columnOrder={columnOrder}
          columnGap={columnGap}
          className={stickyHeader ? styles.stickyHeader : undefined}
          ariaRole="row"
        >
          {columns.map((column) =>
            column.static ? (
              <FiltersSortHeaderStaticColumn
                role="columnheader"
                key={column.value}
                gridColStart={column.colStart}
              >
                <Typography tag="span" variant="p-t7">
                  {column.label}
                </Typography>
              </FiltersSortHeaderStaticColumn>
            ) : (
              <FiltersSortHeaderSortableColumn
                wrapperAriaRole="columnheader"
                key={column.value}
                option={column}
                onClick={handleChangeSorting}
                querySortOption={sortBy}
                querySortDirection={direction}
                gridColStart={column.colStart}
              />
            )
          )}
        </FiltersSortHeaderWrapper>
      )}
      {renderItems(sorted)}
    </Box>
  );
};

export default SortableTable;
